import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, BarChart2, ReceiptText } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './BankruptcyRiskCalculatorPage/components/CalculationForm';
import ResultsOverview from './BankruptcyRiskCalculatorPage/components/ResultsOverview';
import RiskAnalysisChart from './BankruptcyRiskCalculatorPage/components/RiskAnalysisChart';
import QuickAssessmentSidebar from './BankruptcyRiskCalculatorPage/components/QuickAssessmentSidebar';
import ComparisonSidebar from './BankruptcyRiskCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './BankruptcyRiskCalculatorPage/components/TipsSidebar';
import InfoContent from './BankruptcyRiskCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Финансовые показатели
  totalAssets: '1000000',
  totalLiabilities: '750000',
  currentAssets: '400000',
  currentLiabilities: '300000',
  retainedEarnings: '150000',
  ebit: '120000',
  revenue: '900000',
  equity: '250000',
  workingCapital: '100000',
  
  // Тип предприятия и отрасль
  companyType: 'llc',
  industryType: 'retail',
  companyAge: '5',
  
  // Дополнительные параметры
  includeQualitativeFactors: false,
  creditHistory: 'good',
  pendingLawsuits: false,
  managementChanges: false,
  delayedPayments: false,
  supplierRelationships: 'good',
  
  // Расчетная модель
  calculationModel: 'altman-z',
};

// Определение типов предприятий
export const companyTypes = [
  { id: 'individual', name: 'Индивидуальный предприниматель' },
  { id: 'llc', name: 'Общество с ограниченной ответственностью (ООО)' },
  { id: 'jsc', name: 'Акционерное общество (АО)' },
  { id: 'partnership', name: 'Товарищество' },
  { id: 'cooperative', name: 'Кооператив' },
  { id: 'nonprofit', name: 'Некоммерческая организация' }
];

// Определение типов отраслей
export const industryTypes = [
  { id: 'manufacturing', name: 'Производство' },
  { id: 'retail', name: 'Розничная торговля' },
  { id: 'wholesale', name: 'Оптовая торговля' },
  { id: 'services', name: 'Услуги' },
  { id: 'construction', name: 'Строительство' },
  { id: 'agriculture', name: 'Сельское хозяйство' },
  { id: 'it', name: 'Информационные технологии' },
  { id: 'finance', name: 'Финансы и страхование' },
  { id: 'hospitality', name: 'Гостиничный бизнес и общепит' },
  { id: 'transportation', name: 'Транспорт и логистика' },
  { id: 'healthcare', name: 'Здравоохранение' },
  { id: 'realestate', name: 'Недвижимость' }
];

// Определение моделей оценки банкротства
export const bankruptcyModels = [
  { id: 'altman-z', name: 'Модель Альтмана (Z-счет)' },
  { id: 'fulmer', name: 'Модель Фулмера' },
  { id: 'springate', name: 'Модель Спрингейта' },
  { id: 'taffler', name: 'Модель Таффлера' },
  { id: 'beaver', name: 'Модель Бивера' },
  { id: 'saifullin-kadykov', name: 'Модель Сайфуллина-Кадыкова' },
  { id: 'comprehensive', name: 'Комплексная оценка' }
];

/**
 * BankruptcyRiskCalculatorPage Component - Калькулятор оценки риска банкротства для различных типов предприятий.
 */
const BankruptcyRiskCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('bankruptcy-risk-calculator') || {
    id: 'bankruptcy-risk-calculator',
    name: 'Калькулятор риска банкротства',
    metaTitle: 'Калькулятор риска банкротства | Gensio',
    metaDescription: 'Комплексная оценка финансовой устойчивости и риска банкротства компании по различным моделям: Альтмана, Фулмера, Спрингейта, Таффлера и другим.'
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор риска банкротства | Gensio';
  const metaDescription = tool?.metaDescription || 'Комплексная оценка финансовой устойчивости и риска банкротства компании по различным моделям: Альтмана, Фулмера, Спрингейта, Таффлера и другим.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedAssessments, setSavedAssessments] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных оценок из localStorage ---
  useEffect(() => {
    const savedAssessmentsData = localStorage.getItem('savedBankruptcyAssessments');
    if (savedAssessmentsData) {
      try {
        setSavedAssessments(JSON.parse(savedAssessmentsData));
      } catch (e) {
        console.error("Error loading saved assessments:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'altman-z':
          newState.calculationModel = 'altman-z';
          break;
        case 'fulmer':
          newState.calculationModel = 'fulmer';
          break;
        case 'springate':
          newState.calculationModel = 'springate';
          break;
        case 'taffler':
          newState.calculationModel = 'taffler';
          break;
        case 'beaver':
          newState.calculationModel = 'beaver';
          break;
        case 'comprehensive':
          newState.calculationModel = 'comprehensive';
          break;
        case 'manufacturing':
          newState.industryType = 'manufacturing';
          break;
        case 'retail':
          newState.industryType = 'retail';
          break;
        case 'services':
          newState.industryType = 'services';
          break;
        case 'small-business':
          newState.companyType = 'individual';
          newState.totalAssets = '500000';
          newState.totalLiabilities = '300000';
          newState.currentAssets = '200000';
          newState.currentLiabilities = '150000';
          newState.retainedEarnings = '50000';
          newState.ebit = '80000';
          newState.revenue = '450000';
          break;
        case 'qualitative':
          newState.includeQualitativeFactors = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setAnalysisData(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение результатов оценки в localStorage
  const saveAssessment = (name) => {
    if (!results) return;

    const newSavedAssessment = {
      id: Date.now().toString(),
      name: name || `Оценка компании ${formData.companyType} (${formData.industryType})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      calculationModel: formData.calculationModel
    };

    const updatedSavedAssessments = [...savedAssessments, newSavedAssessment];
    setSavedAssessments(updatedSavedAssessments);
    localStorage.setItem('savedBankruptcyAssessments', JSON.stringify(updatedSavedAssessments));

    return newSavedAssessment;
  };

  // Удаление сохраненной оценки
  const deleteSavedAssessment = (id) => {
    const updatedSavedAssessments = savedAssessments.filter(assessment => assessment.id !== id);
    setSavedAssessments(updatedSavedAssessments);
    localStorage.setItem('savedBankruptcyAssessments', JSON.stringify(updatedSavedAssessments));
  };

  // Загрузка сохраненной оценки
  const loadSavedAssessment = (id) => {
    const assessmentToLoad = savedAssessments.find(assessment => assessment.id === id);
    if (assessmentToLoad) {
      setFormData(assessmentToLoad.formData);
      setResults(assessmentToLoad.results);
      setAnalysisData(assessmentToLoad.analysisData || null);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор риска банкротства'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setAnalysisData={setAnalysisData}
              companyTypes={companyTypes}
              industryTypes={industryTypes}
              bankruptcyModels={bankruptcyModels}
              saveAssessment={saveAssessment}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                {analysisData && (
                  <RiskAnalysisChart 
                    analysisData={analysisData} 
                    calculationModel={formData.calculationModel}
                  />
                )}
              </>
            )}

            {/* --- Информационный контент о банкротстве --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой оценки --- */}
            <QuickAssessmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              companyTypes={companyTypes}
              industryTypes={industryTypes}
              bankruptcyModels={bankruptcyModels}
            />

            {/* --- Панель сравнения оценок --- */}
            <ComparisonSidebar 
              savedAssessments={savedAssessments}
              deleteSavedAssessment={deleteSavedAssessment}
              loadSavedAssessment={loadSavedAssessment}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-orange-500 focus:ring-orange-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-orange-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-orange-600 focus:ring-orange-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Styles for Risk Indicators */
        .risk-indicator {
          @apply relative rounded-full h-4 w-20 overflow-hidden;
        }
        .risk-indicator-low {
          @apply bg-gradient-to-r from-green-300 to-green-500;
        }
        .risk-indicator-medium {
          @apply bg-gradient-to-r from-yellow-300 to-yellow-500;
        }
        .risk-indicator-high {
          @apply bg-gradient-to-r from-orange-300 to-red-500;
        }
        .risk-indicator-critical {
          @apply bg-gradient-to-r from-red-500 to-red-700;
        }
      `}</style>
    </div>
  );
};

export default BankruptcyRiskCalculatorPage;